import React, { useEffect, useRef, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';
import { transitions } from '../../motion';

// A table cell you can type in.
//
// The list is the only screen that shows forty people at once, and fixing
// a phone number in it meant opening a record, editing, and coming back —
// forty times. This keeps the table a table: it reads as text until you
// click it, and it is an input while you are in it.
//
// Deliberately NOT an always-on TextField per cell. Forty rows of visible
// input boxes is a wall of chrome, it makes the row height jump, and it
// invites a stray keystroke into somebody's SSN.

export interface EditableCellProps {
  value: string | number | null | undefined;
  type?: 'text' | 'number' | 'date';
  placeholder?: string;
  disabled?: boolean;
  /** Only called when the value actually changed. */
  onSave: (next: string) => void;
  /** How to draw it when it is not being edited. */
  render?: (value: string) => React.ReactNode;
  maxLength?: number;
}

const EditableCell: React.FC<EditableCellProps> = ({
  value, type = 'text', placeholder, disabled, onSave, render, maxLength,
}) => {
  const initial = value == null ? '' : String(value);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initial);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => { setDraft(initial); }, [initial]);
  useEffect(() => { if (editing) input.current?.focus(); }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft === initial) return;
    onSave(draft);
  };

  if (editing && !disabled) {
    return (
      <TextField
        inputRef={input}
        size="small"
        type={type}
        value={draft}
        placeholder={placeholder}
        inputProps={maxLength ? { maxLength } : undefined}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(); }
          // Escape puts it back the way it was, which is the whole reason
          // a person is willing to click into a cell at all.
          if (e.key === 'Escape') { setDraft(initial); setEditing(false); }
          e.stopPropagation();
        }}
        onClick={(e) => e.stopPropagation()}
        sx={{ width: '100%', '& .MuiInputBase-input': { py: 0.5, fontSize: 13 } }}
      />
    );
  }

  return (
    <Box
      role={disabled ? undefined : 'button'}
      tabIndex={disabled ? undefined : 0}
      onClick={(e) => { if (disabled) return; e.stopPropagation(); setEditing(true); }}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setEditing(true); }
      }}
      sx={{
        minHeight: 26,
        display: 'flex',
        alignItems: 'center',
        px: 0.75,
        mx: -0.75,
        borderRadius: 1,
        cursor: disabled ? 'default' : 'text',
        transition: transitions(['background-color', 'box-shadow']),
        ...(disabled ? {} : {
          '&:hover': { bgcolor: 'action.hover', boxShadow: '0 0 0 1px var(--topo-divider, rgba(0,0,0,0.12)) inset' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
        }),
      }}
    >
      {initial
        ? (render ? render(initial) : (
          <Typography variant="body2" noWrap>{initial}</Typography>
        ))
        : (
          <Typography variant="body2" color="text.disabled" noWrap>
            {placeholder || '—'}
          </Typography>
        )}
    </Box>
  );
};

export default EditableCell;
